import React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { findBlock, locateBlockById } from "../domain/find";
import { selectTextEditingBlock } from "../domain/selectors";
import { useEditorStore } from "../domain/store";
import { HEADING_FONT_WEIGHT_BOLD, toggleEmailBuilderHeadingWeight } from "../editing/richText/emailBuilderHeadingBold";
import { type TEmailBuilderCanvasBlockRailAction } from "../editing/emailBuilderCanvasBlockInteractions";
import { EmailBuilderTextLinkPopover } from "../editing/EmailBuilderTextLinkPopover";
import { isEmailBuilderLinkUrlFieldValueValid } from "../operations/emailBuilderPublishLinkScan";
import { resolveEmailBuilderInlineTextAppliedHref } from "../operations/emailTextLinkEditing";
import {
  emailBuilderRichTextCaretInMergeTag,
  getEmailBuilderRichTextEditor,
  subscribeEmailBuilderRichTextSession,
  toggleEmailBuilderRichTextMergeTagBrackets,
} from "../editing/richText/emailBuilderRichTextSession";
import {
  resolveEmailBuilderTextLinkPopoverPosition,
  type IEmailBuilderTextLinkPopoverPosition,
} from "../editing/emailBuilderTextLinkPopoverPosition";

type FormattingRailActiveState = {
  mergeTag: boolean;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  underlineDisabled: boolean;
  strikethrough: boolean;
  bulletedList: boolean;
  numberedList: boolean;
  link: boolean;
};

const EMPTY_ACTIVE_STATE: FormattingRailActiveState = {
  mergeTag: false,
  bold: false,
  italic: false,
  underline: false,
  underlineDisabled: false,
  strikethrough: false,
  bulletedList: false,
  numberedList: false,
  link: false,
};

export function useFormattingRail(blockId: string) {
  const { t } = useTranslation();
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const headingFontWeight = useEditorStore(state => {
    const located = locateBlockById(state.document, blockId);
    if (located === undefined) {
      return null;
    }
    const block = findBlock(
      state.document,
      located.rowId,
      located.columnId,
      located.blockId,
    );
    if (block?.type !== "text" || block.variant === "normal") {
      return null;
    }
    return block.fontWeight;
  });
  const railRef = useRef<HTMLDivElement | null>(null);
  const [sessionTick, setSessionTick] = useState(0);
  const [activeState, setActiveState] = useState<FormattingRailActiveState>(EMPTY_ACTIVE_STATE);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkHref, setLinkHref] = useState("");
  const [linkHrefError, setLinkHrefError] = useState<string | null>(null);
  const [linkEditingExisting, setLinkEditingExisting] = useState(false);
  const [linkPopoverPosition, setLinkPopoverPosition] =
    useState<IEmailBuilderTextLinkPopoverPosition | null>(null);

  const enabled =
    textEditingBlock !== null && textEditingBlock.blockId === blockId;

  useEffect(() => subscribeEmailBuilderRichTextSession(() => {
    setSessionTick(tick => tick + 1);
  }), []);

  const syncLinkPopoverPosition = useCallback((): boolean => {
    const anchorEl = railRef.current;
    if (anchorEl === null) {
      return false;
    }

    setLinkPopoverPosition(
      resolveEmailBuilderTextLinkPopoverPosition({
        anchorRect: anchorEl.getBoundingClientRect(),
        placement: "below",
      }),
    );
    return true;
  }, []);

  useLayoutEffect(() => {
    if (!linkPopoverOpen) {
      setLinkPopoverPosition(null);
      return;
    }

    let rafId = 0;

    function handleReposition() {
      if (!syncLinkPopoverPosition()) {
        rafId = window.requestAnimationFrame(handleReposition);
      }
    }

    handleReposition();

    function handleViewportChange() {
      syncLinkPopoverPosition();
    }

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [linkPopoverOpen, syncLinkPopoverPosition]);

  useEffect(() => {
    const editor = enabled ? getEmailBuilderRichTextEditor(blockId) : null;
    if (editor === null) {
      setActiveState(EMPTY_ACTIVE_STATE);
      setLinkPopoverOpen(false);
      setLinkEditingExisting(false);
      return;
    }

    function syncActiveState() {
      const current = getEmailBuilderRichTextEditor(blockId);
      if (current === null) {
        return;
      }

      const linkActive = current.isActive("link");
      setActiveState({
        mergeTag: emailBuilderRichTextCaretInMergeTag(current),
        bold: headingFontWeight === null
          ? current.isActive("bold")
          : headingFontWeight >= HEADING_FONT_WEIGHT_BOLD,
        italic: current.isActive("italic"),
        underline: current.isActive("underline"),
        underlineDisabled: linkActive,
        strikethrough: current.isActive("strike"),
        bulletedList: current.isActive("bulletList"),
        numberedList: current.isActive("orderedList"),
        link: linkActive,
      });
    }

    syncActiveState();
    editor.on("selectionUpdate", syncActiveState);
    editor.on("transaction", syncActiveState);
    return () => {
      editor.off("selectionUpdate", syncActiveState);
      editor.off("transaction", syncActiveState);
    };
  }, [blockId, enabled, headingFontWeight, sessionTick]);

  const closeLinkPopover = useCallback(() => {
    setLinkPopoverOpen(false);
    setLinkEditingExisting(false);
    setLinkPopoverPosition(null);
    setLinkHrefError(null);
    getEmailBuilderRichTextEditor(blockId)?.commands.focus();
  }, [blockId]);

  const openLinkPopover = useCallback(() => {
    const editor = getEmailBuilderRichTextEditor(blockId);
    const href = editor?.getAttributes("link").href;
    setLinkEditingExisting(editor?.isActive("link") === true);
    setLinkHref(typeof href === "string" ? href : "");
    setLinkHrefError(null);
    setLinkPopoverOpen(true);
  }, [blockId]);

  const handleAction = useCallback(
    (action: TEmailBuilderCanvasBlockRailAction) => {
      const editor = getEmailBuilderRichTextEditor(blockId);
      if (!enabled || editor === null) {
        return;
      }

      const chain = () => editor.chain().focus();

      switch (action) {
        case "merge-tag":
          setLinkPopoverOpen(false);
          toggleEmailBuilderRichTextMergeTagBrackets(blockId);
          return;
        case "bold":
          if (!toggleEmailBuilderHeadingWeight(editor)) {
            chain().toggleBold().run();
          }
          return;
        case "italic":
          chain().toggleItalic().run();
          return;
        case "underline":
          if (editor.isActive("link")) {
            return;
          }
          chain().toggleUnderline().run();
          return;
        case "strikethrough":
          chain().toggleStrike().run();
          return;
        case "bulleted-list":
          chain().toggleBulletList().run();
          return;
        case "numbered-list":
          chain().toggleOrderedList().run();
          return;
        case "link":
          openLinkPopover();
          return;
        default:
          return;
      }
    },
    [blockId, enabled, openLinkPopover],
  );

  const handleActionMouseDown = useCallback(
    (action: TEmailBuilderCanvasBlockRailAction, event: ReactMouseEvent) => {
      if (
        action === "bold" ||
        action === "italic" ||
        action === "underline" ||
        action === "strikethrough" ||
        action === "bulleted-list" ||
        action === "numbered-list" ||
        action === "link" ||
        action === "merge-tag"
      ) {
        event.preventDefault();
      }
    },
    [],
  );

  const isActionActive = useCallback(
    (action: TEmailBuilderCanvasBlockRailAction): boolean => {
      switch (action) {
        case "merge-tag":
          return activeState.mergeTag;
        case "bold":
          return activeState.bold;
        case "italic":
          return activeState.italic;
        case "underline":
          return activeState.underline;
        case "strikethrough":
          return activeState.strikethrough;
        case "bulleted-list":
          return activeState.bulletedList;
        case "numbered-list":
          return activeState.numberedList;
        case "link":
          return linkPopoverOpen || activeState.link;
        default:
          return false;
      }
    },
    [activeState, linkPopoverOpen],
  );

  const isActionDisabled = useCallback(
    (action: TEmailBuilderCanvasBlockRailAction): boolean => {
      if (action === "underline") {
        return activeState.underlineDisabled;
      }

      return false;
    },
    [activeState.underlineDisabled],
  );

  const linkPopover =
    linkPopoverOpen && linkPopoverPosition !== null ? (
      <EmailBuilderTextLinkPopover
        href={linkHref}
        editingExistingLink={linkEditingExisting}
        hrefError={linkHrefError}
        position={linkPopoverPosition}
        onHrefChange={nextHref => {
          setLinkHref(nextHref);
          if (linkHrefError !== null) {
            setLinkHrefError(null);
          }
        }}
        onHrefBlur={() => {
          if (linkHref.trim() === "" || isEmailBuilderLinkUrlFieldValueValid(linkHref)) {
            setLinkHrefError(null);
            return;
          }
          setLinkHrefError(
            t("Enter a valid URL (e.g. example.com or https://…) or merge tag."),
          );
        }}
        onApply={() => {
          const editor = getEmailBuilderRichTextEditor(blockId);
          if (editor === null) {
            closeLinkPopover();
            return;
          }

          if (linkEditingExisting && linkHref.trim() === "") {
            editor.chain().focus().unsetLink().run();
            closeLinkPopover();
            return;
          }

          if (linkHref.trim() !== "" && !isEmailBuilderLinkUrlFieldValueValid(linkHref)) {
            setLinkHrefError(
              t("Enter a valid URL (e.g. example.com or https://…) or merge tag."),
            );
            return;
          }

          const href = resolveEmailBuilderInlineTextAppliedHref(linkHref);
          editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
          closeLinkPopover();
        }}
        onCancel={closeLinkPopover}
      />
    ) : null;

  return {
    railRef,
    handleAction,
    handleActionMouseDown,
    isActionActive,
    isActionDisabled,
    linkPopover,
    linkPopoverOpen,
  };
}
